import type { Order, OrderStatus, Product } from './types';
import { productUnitsMap } from './productUnits';

type ProductUnit = {
  value: number;
  unit: string;
  label: string;
};

export type OrderExportRow = {
  orderId: string;
  companyName: string;
  orgId: string;
  productName: string;
  productId: string;
  packages: number | null;
  packageSize: string;
  totalQuantity: number | null;
  unit: string;
  status: string;
  deliveryDate: Date | null;
  nextExpectedDeliveryDate: Date | null;
  createdAt: Date;
  createdBy: string;
  deliveryAddress: string;
  deliveryZip: string;
  deliveryCity: string;
  deliveryAtt: string;
  contactPerson: string;
  orderReference: string;
  invoiceEmail: string;
  invoiceNumber: string;
  eanNumber: string;
  cvrNumber: string;
  intermediateStorage: string;
  returnedContainers: string;
  internalNote: string;
};

const productUnitOptions = productUnitsMap as Record<string, ProductUnit[]>;

const statusLabels: Record<OrderStatus, string> = {
  AWAITING: 'Afventer godkendelse',
  AWAITING_DISTRIBUTION: 'Afventer distribution',
  PROCESSING: 'Behandles',
  READY_FOR_SHIPPING: 'Klar til afsendelse',
  IN_TRANSIT: 'Afsendt',
  AWAITING_INVOICE: 'Afventer faktura',
  COMPLETED: 'Afsluttet',
  VOIDED: 'Annulleret',
};

function getProductValues(product: Product) {
  const selectedUnit = productUnitOptions[product.id]?.find(
    (option) => String(option.value) === String(product.unit)
  );

  return {
    packages: product.quantity,
    packageSize: selectedUnit?.label || (product.unit ? String(product.unit) : ''),
    totalQuantity: selectedUnit ? product.quantity * selectedUnit.value : product.quantity,
    unit: selectedUnit?.unit || '',
  };
}

function getBaseOrderValues(order: Order) {
  return {
    orderId: order.id,
    companyName: order.companyName,
    orgId: order.orgId,
    status: statusLabels[order.status] || order.status,
    deliveryDate: order.deliveryDate || null,
    nextExpectedDeliveryDate: order.nextExpectedDeliveryDate || null,
    createdAt: order.createTime,
    createdBy: order.createdBy?.displayName || '',
    deliveryAddress: order.deliveryAddress || '',
    deliveryZip: order.deliveryZip || '',
    deliveryCity: order.deliveryCity || '',
    deliveryAtt: order.deliveryAtt || '',
    contactPerson: order.contactPerson || '',
    orderReference: order.orderRef || '',
    invoiceEmail: order.invoiceEmail || '',
    invoiceNumber: order.invoiceNo || '',
    eanNumber: order.eanNumber || '',
    cvrNumber: order.cvrNumber || '',
    intermediateStorage: order.intermediateStorage || '',
    returnedContainers: (order.returnedContainers || [])
      .map((container) => `${container.quantity} × ${container.type}`)
      .join(', '),
    internalNote: order.internalNote || '',
  };
}

export function buildOrderExportRows(orders: Order[]): OrderExportRow[] {
  return orders.flatMap<OrderExportRow>((order): OrderExportRow[] => {
    const baseValues = getBaseOrderValues(order);

    if (!order.products?.length) {
      return [
        {
          ...baseValues,
          productName: '',
          productId: '',
          packages: null,
          packageSize: '',
          totalQuantity: null,
          unit: '',
        },
      ];
    }

    return order.products.map((product) => ({
      ...baseValues,
      productName: product.name || '',
      productId: product.id,
      ...getProductValues(product),
    }));
  });
}

export async function createOrderWorkbook(orders: Order[]) {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Ordrer', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  workbook.creator = 'aks2tal';
  workbook.created = new Date();

  worksheet.columns = [
    { header: 'Ordre-ID', key: 'orderId', width: 22 },
    { header: 'Firma', key: 'companyName', width: 30 },
    { header: 'Org-ID', key: 'orgId', width: 22 },
    { header: 'Produkt', key: 'productName', width: 30 },
    { header: 'Produkt-ID', key: 'productId', width: 24 },
    { header: 'Antal enheder', key: 'packages', width: 14 },
    { header: 'Enhedsstørrelse', key: 'packageSize', width: 18 },
    { header: 'Samlet mængde', key: 'totalQuantity', width: 16 },
    { header: 'Måleenhed', key: 'unit', width: 13 },
    { header: 'Status', key: 'status', width: 22 },
    { header: 'Leveringsdato', key: 'deliveryDate', width: 16 },
    { header: 'Næste forventede levering', key: 'nextExpectedDeliveryDate', width: 24 },
    { header: 'Oprettet', key: 'createdAt', width: 18 },
    { header: 'Oprettet af', key: 'createdBy', width: 22 },
    { header: 'Leveringsadresse', key: 'deliveryAddress', width: 30 },
    { header: 'Postnr.', key: 'deliveryZip', width: 11 },
    { header: 'By', key: 'deliveryCity', width: 20 },
    { header: 'Att.', key: 'deliveryAtt', width: 22 },
    { header: 'Kontaktperson', key: 'contactPerson', width: 22 },
    { header: 'Ordre-reference', key: 'orderReference', width: 20 },
    { header: 'Faktura-email', key: 'invoiceEmail', width: 28 },
    { header: 'Fakturanr.', key: 'invoiceNumber', width: 16 },
    { header: 'EAN-nr.', key: 'eanNumber', width: 18 },
    { header: 'CVR-nr.', key: 'cvrNumber', width: 14 },
    { header: 'Mellemlager', key: 'intermediateStorage', width: 18 },
    { header: 'Returemballage', key: 'returnedContainers', width: 24 },
    { header: 'Intern note', key: 'internalNote', width: 40 },
  ];

  worksheet.addRows(buildOrderExportRows(orders));
  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: worksheet.columns.length },
  };

  const headerRow = worksheet.getRow(1);
  headerRow.height = 24;
  headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF1455A6' },
  };
  headerRow.alignment = { vertical: 'middle' };

  ['packages', 'totalQuantity'].forEach((key) => {
    worksheet.getColumn(key).numFmt = '#,##0.##';
  });
  ['deliveryDate', 'nextExpectedDeliveryDate'].forEach((key) => {
    worksheet.getColumn(key).numFmt = 'yyyy-mm-dd';
  });
  worksheet.getColumn('createdAt').numFmt = 'yyyy-mm-dd hh:mm';
  worksheet.getColumn('internalNote').alignment = { vertical: 'top', wrapText: true };

  return workbook;
}

export async function exportOrdersToExcel(orders: Order[]) {
  const workbook = await createOrderWorkbook(orders);

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer as BlobPart], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `ordrer-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
